import classNames from 'classnames'
import i18next from 'i18next'
import React, { ReactNode, useEffect, useId, useRef, useState } from 'react'
import { FormCheckbox } from '../form-checkbox'
import { IconFa } from '../icon-fa'
import { Menu, MenuButton, MenuItem, MenuItems } from '../menu'
import { Tooltip } from '../tooltip'
import { LinkRow } from './LinkRow'

export interface TableCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  label?: ReactNode
  children?: ReactNode
  id?: string
  truncateClassName?: string
  tooltip?: boolean
  tableHeader?: string
}

export const TableHeaderCell = ({ label, children, tooltip, id, truncateClassName, ...props }: TableCellProps) => (
  <th {...props} className={classNames(props.className, 'pl-3!')}>
    {tooltip && id ? (
      <Tooltip className="w-full align-bottom" offset={{ y: 30, x: 0 }} referenceId={id} label={label}>
        <div className={classNames('text-truncate', truncateClassName)} id={id}>
          {children || label}
        </div>
      </Tooltip>
    ) : (
      children || label
    )}
  </th>
)

export const TableBodyCell = ({ className, label, children, tooltip, id, ...props }: TableCellProps) => {
  return (
    <td {...props} className={className}>
      {tooltip && id ? (
        <Tooltip className="w-full align-bottom" offset={{ y: 30, x: 0 }} referenceId={id} label={label || children}>
          <div className="text-truncate w-[95%]" id={id}>
            {children || label}
          </div>
        </Tooltip>
      ) : (
        children || label
      )}
    </td>
  )
}

export const TableBodyCellAutoTooltip = ({
  className,
  children,
  id,
  label,
  ...props
}: Omit<TableCellProps, 'tooltip'>) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const tooltipId = useId()

  useEffect(() => {
    const element = contentRef.current
    if (element) {
      setIsOverflowing(element.scrollWidth > element.clientWidth)
    }
  }, [children])

  const content = (
    <div ref={contentRef} className="text-truncate w-[95%]" id={id || tooltipId}>
      {children}
    </div>
  )

  return (
    <td {...props} className={className}>
      {isOverflowing ? (
        <Tooltip
          className="w-full align-bottom"
          offset={{ y: 30, x: 0 }}
          referenceId={id || tooltipId}
          label={label || children}
        >
          {content}
        </Tooltip>
      ) : (
        content
      )}
    </td>
  )
}

export const TableBodyRow = ({
  onClick,
  selected,
  children,
  className,
  linked = false
}: {
  onClick?: (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void
  selected?: boolean
  children: ReactNode
  className?: string
  linked?: boolean
}) => {
  const Row = linked ? LinkRow : 'tr'
  return (
    <Row
      onClick={onClick}
      className={classNames(
        (linked || Boolean(onClick)) && 'cursor-pointer hover:bg-gray-100',
        selected && 'bg-primary-100 border-primary-500! hover:bg-primary-100',
        className
      )}
    >
      {children}
    </Row>
  )
}

interface SortedHeaderCellProps extends TableCellProps {
  onClick: () => void
  sortDirection?: 'asc' | 'desc'
  children: ReactNode
  cellClassName?: string
}

export const SortedHeaderCell = ({
  onClick,
  children,
  label,
  tooltip,
  id,
  className,
  sortDirection,
  truncateClassName,
  cellClassName,
  ...props
}: SortedHeaderCellProps) => {
  return (
    <th {...props} className={cellClassName} onClick={onClick}>
      <div
        className={classNames(
          'cursor-pointer w-[calc(100%-4px)] max-w-fit items-center inline-flex py-2 pr-2 hover:pl-2 hover:ml-[-8px] hover:bg-gray-100 mr-1  hover:rounded-xs hover:shadow-gray-500',
          className
        )}
      >
        <IconFa
          icon={['far', sortDirection ? (sortDirection === 'asc' ? 'arrow-up' : 'arrow-down') : 'arrows-up-down']}
          className="w-4 mr-2"
        />
        {tooltip && id ? (
          <Tooltip className="w-[calc(100%-4px)] align-bottom" offset={{ y: 30, x: 0 }} referenceId={id} label={label}>
            <div className={classNames('text-truncate', truncateClassName)} id={id}>
              {children || label}
            </div>
          </Tooltip>
        ) : (
          children || label
        )}
      </div>
    </th>
  )
}

interface ExpandableTrProps extends Omit<React.HTMLAttributes<HTMLTableRowElement>, 'children'> {
  expanded: boolean
  childrenExpanded?: ReactNode
  children?: ReactNode | ((expanded: boolean) => ReactNode)
  classNameExpanded?: string
}

export const ExpandableTr = ({
  expanded,
  children,
  childrenExpanded,
  classNameExpanded,
  ...props
}: ExpandableTrProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  useEffect(() => {
    childrenExpanded && setIsExpanded(expanded)
  }, [expanded])

  return (
    <>
      <tr {...props} className={isExpanded ? classNameExpanded : props.className}>
        {childrenExpanded ? (
          <td
            onClick={(e) => {
              e.stopPropagation()
              setIsExpanded(!isExpanded)
            }}
            className={'cursor-pointer'}
          >
            <IconFa icon={['far', isExpanded || isExpanded ? 'chevron-down' : 'chevron-right']} className="w-3" />
          </td>
        ) : (
          <td />
        )}
        {typeof children === 'function' ? children(isExpanded) : children}
      </tr>
      {isExpanded && childrenExpanded}
    </>
  )
}

export const TableHeaderCheckboxMenu = ({
  allSelected,
  allOnPageSelected,
  handleSelectCurrentPage,
  handleSelectAll,
  handleDeselectAll,
  itemsOnPage,
  totalItems,
  selectedItems
}: {
  allSelected: boolean
  allOnPageSelected: boolean
  handleSelectCurrentPage: () => void
  handleSelectAll?: () => void
  handleDeselectAll: () => void
  itemsOnPage: number
  totalItems?: number
  selectedItems: number
}) => (
  <Menu positionStrategy="fixed">
    <MenuButton className="p-0 border-none ">
      {({ open }: { open: boolean }) => (
        <div
          className={classNames(
            'flex items-center p-[6px] pr-2 gap-4 rounded-xs hover:bg-gray-300',
            open ? 'bg-gray-300' : 'bg-transparent'
          )}
        >
          <FormCheckbox className="pointer-events-none" checked={allSelected} onChange={() => {}} />
          <IconFa icon={['far', 'chevron-down']} size="xs" />
        </div>
      )}
    </MenuButton>

    <MenuItems>
      <MenuItem onClick={handleSelectCurrentPage} disabled={allOnPageSelected}>
        {i18next.t('glow.selectAllOnPage', {
          count: itemsOnPage
        })}
      </MenuItem>
      {handleSelectAll ? (
        <MenuItem onClick={handleSelectAll} disabled={allSelected}>
          {i18next.t('glow.selectAllCount', {
            count: totalItems
          })}
        </MenuItem>
      ) : null}
      <MenuItem onClick={handleDeselectAll} disabled={selectedItems === 0}>
        {i18next.t('glow.deselectAll')}
      </MenuItem>
    </MenuItems>
  </Menu>
)
